// Translates Action mutations into Supabase DB writes. Called by
// SupabaseStore.dispatch after the reducer has computed an optimistic next state.
//
// Actions fall into three buckets:
//   1. Direct writes (RLS-gated)  — implemented here, fire-and-forget per row
//   2. Edge Function (transactional) — stubbed; logs a warning until Phase 4
//   3. Local-only (UI ephemeral)   — no-op; reducer's local update is enough
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Action, AppState, Match, MatchEvent } from '../types';
import { ROBOTS_BY_EVENT, robotClassLabel } from '../types';

export interface AuthContext {
  userId: string | null;
  email: string | null;
  role: 'staff' | 'ops' | null;
}

export async function readAuthContext(supabase: SupabaseClient): Promise<AuthContext> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return { userId: null, email: null, role: null };
  let role: AuthContext['role'] = null;
  try {
    const payload = JSON.parse(atob(session.access_token.split('.')[1]!));
    if (payload.app_role === 'volunteer' || payload.app_role === 'staff' || payload.app_role === 'ops') role = payload.app_role;
  } catch {
    // Malformed JWT; treat as no role.
  }
  return { userId: session.user.id, email: session.user.email ?? null, role };
}

const FIELD_TO_DB: Record<string, string> = {
  spielGiven: 'spiel_given',
  badges: 'badges',
  badges1v1: 'badges_1v1',
  badges3v3: 'badges_3v3',
  badgesEc:  'badges_ec',
  borrowing: 'borrowing',
  checkInForm: 'check_in_form',
  pointsPenalty3v3: 'points_penalty_3v3',
  pointsPenaltyEc: 'points_penalty_ec',
  hasBackupRobot: 'has_backup_robot',
};

// RobotPhotoSet field → team_pictures column. Keep in sync with the
// RobotPhotoSet type + SET_TEAM_PICTURE action.
const PICTURE_FIELD_TO_COL: Record<string, string> = {
  robot: 'robot',
  driver: 'driver',
  driverName: 'driver_name',
  driverPronouns: 'driver_pronouns',
  chief: 'chief',
  chiefName: 'chief_name',
  chiefPronouns: 'chief_pronouns',
};


/**
 * Apply an Action's effects to Supabase. The reducer has already computed `next`
 * so we can read deltas off it (e.g. event_state.officialDelay) without
 * re-deriving here. Throws on DB failure; caller handles revert.
 */
export async function applyAction(
  supabase: SupabaseClient,
  action: Action,
  prev: AppState,
  next: AppState,
  auth: AuthContext,
): Promise<void> {
  switch (action.type) {
    // ============== Direct writes ==============

    case 'ADD_DELAY':
    case 'CLEAR_DELAY': {
      // Delay value + block anchor are written together so the persisted
      // state is always coherent. The reducer set both based on the current
      // block (block-aware reset on block change); we mirror its decision.
      const minutesCol = action.event === '3v3' ? 'official_delay_3v3_minutes' : 'official_delay_1v1_minutes';
      const blockCol   = action.event === '3v3' ? 'official_delay_3v3_block_id' : 'official_delay_1v1_block_id';
      await wrap(supabase.from('event_state')
        .update({
          [minutesCol]: next.officialDelay[action.event],
          [blockCol]:   next.officialDelayBlock[action.event],
        })
        .eq('id', 'current'));
      return;
    }

    case 'UPDATE_CHECKIN': {
      const dbField = FIELD_TO_DB[action.field as string];
      // Surface unmapped fields loudly: silently dropping a write means the
      // optimistic UI shows the change but the DB never gets it. If you add
      // a new CheckInRow field, also add it to FIELD_TO_DB above.
      if (!dbField) {
        throw new Error(
          `UPDATE_CHECKIN: no DB column mapping for field '${String(action.field)}'. ` +
          `Add it to FIELD_TO_DB in src/store/supabase-actions.ts.`,
        );
      }
      await wrap(supabase.from('check_in').upsert({
        team_id: action.team,
        [dbField]: action.value,
        updated_by: auth.userId,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'team_id' }));
      return;
    }

    case 'TOGGLE_FIELD_CALIBRATION': {
      // Ops-gated upsert into field_calibration, keyed on (team_id, event).
      // RLS enforces the ops role; the audit trigger records who flipped it.
      await wrap(supabase.from('field_calibration').upsert({
        team_id: action.team,
        event: action.event,
        done: action.done,
        set_by: auth.userId,
        set_at: new Date().toISOString(),
      }, { onConflict: 'team_id,event' }));
      return;
    }

    case 'SET_TEAM_PICTURE': {
      // Vol+-gated upsert into team_pictures, keyed on (team_id, event,
      // robot_class). RLS enforces the volunteer/staff/ops role. One column
      // per edit; the rest take their existing value (or column default on a
      // fresh row). Field → column map mirrors RobotPhotoSet.
      const col = PICTURE_FIELD_TO_COL[action.field as string];
      if (!col) {
        throw new Error(
          `SET_TEAM_PICTURE: no DB column mapping for field '${String(action.field)}'. ` +
          'Add it to PICTURE_FIELD_TO_COL in src/store/supabase-actions.ts.',
        );
      }
      await wrap(supabase.from('team_pictures').upsert({
        team_id: action.team,
        event: action.event,
        robot_class: action.robot,
        [col]: action.value,
        set_by: auth.userId,
        set_at: new Date().toISOString(),
      }, { onConflict: 'team_id,event,robot_class' }));
      return;
    }

    case 'ADD_WARNING': {
      // RLS requires issued_by = auth.uid(), issued_by_role = current
      // app_role, and issued_by_email = the JWT-claimed email (snapshot
      // for display + audit durability — see migration 41).
      if (!auth.userId || !auth.role || !auth.email) {
        throw new Error('Issuing a warning requires authentication.');
      }
      const w = action.warning;
      await wrap(supabase.from('warnings').insert({
        match_id: w.match || null,
        team_id: w.team,
        code: w.code || null,
        severity: w.severity,
        note: w.note || null,
        issued_by: auth.userId,
        issued_by_role: auth.role,
        issued_by_email: auth.email.toLowerCase(),
      }));
      return;
    }

    case 'MARK_ROBOT': {
      const ai = prev.activeInspection;
      const result = action.result;
      if (ai.currentStation === 'l3') return; // L3 doesn't write robot results
      // Look up the SPECIFIC queue row by id. activeInspection.queueId
      // disambiguates primary vs backup of the same robot_class — both
      // share team+match+robotClass but live in distinct queue rows.
      // Without queueId we silently mutated the first matching row in
      // the DB (typically the primary), leaving stale busy_at_station
      // on the actually-claimed row and hiding the team from L3 review.
      const activeQueueItem = prev.queue.find((q) => q.id === ai.queueId);
      if (!activeQueueItem) {
        throw new Error(`MARK_ROBOT: no queue row id=${ai.queueId} for team ${ai.team} match ${ai.match} robot ${ai.robotClass}`);
      }
      // Where the per-station result lives depends on whether this is a
      // primary or backup row:
      //   - Primary: robot_inspection_station (persistent rollup, keyed
      //     per MATCH: (team, robot_class, station, match_id)). Upsert —
      //     rows are created on first mark (no seed) and archived
      //     (completed_at set) on match-end.
      //   - Backup: inspection_queue.{s1,s2,s3}_result on this row only.
      //     Backups share the rollup key with their primary, so writing
      //     there would corrupt the primary's progress. Per-row columns
      //     scope the result to this match (queue row is dropped when
      //     the team exits active inspection — fresh re-inspect each match,
      //     per ARC rules).
      // FAIL is destructive across all three stations — a failed mech
      // check invalidates downstream wiring/software passes. Wipe the
      // other stations' results too so the row re-enters at S1.
      const wipeOthers = result === 'fail';
      // Backups AND pre-inspection rows keep progress in their own row columns
      // (s{1,2,3}_result) — they don't feed the per-event rollup table.
      if (activeQueueItem.isBackup || activeQueueItem.preInspection) {
        const column = `${ai.currentStation}_result`;
        const updates: Record<string, unknown> = wipeOthers
          ? { s1_result: null, s2_result: null, s3_result: null, [column]: result }
          : { [column]: result };
        await wrap(supabase.from('inspection_queue')
          .update(updates)
          .eq('id', ai.queueId));
      } else {
        // Per-match key: upsert (create on first mark, reactivate an archived
        // row on re-inspection by clearing completed_at). onConflict = the new PK.
        await wrap(supabase.from('robot_inspection_station')
          .upsert({
            team_id: ai.team,
            robot_class: ai.robotClass,
            station: ai.currentStation,
            match_id: activeQueueItem.match,
            event: activeQueueItem.event,
            result,
            marked_by: auth.userId,
            marked_at: new Date().toISOString(),
            completed_at: null,
          }, { onConflict: 'team_id,robot_class,station,match_id' }));
        if (wipeOthers) {
          // Reset the other two stations to 'pending' so the rollup collapses
          // back to L0 — robot has to clear S1/S2/S3 fresh. Scoped to THIS match.
          // ai.currentStation is the failed station (already 'fail').
          const others = (['s1', 's2', 's3'] as const).filter((s) => s !== ai.currentStation);
          await wrap(supabase.from('robot_inspection_station')
            .upsert(others.map((station) => ({
              team_id: ai.team,
              robot_class: ai.robotClass,
              station,
              match_id: activeQueueItem.match,
              event: activeQueueItem.event,
              result: 'pending' as const,
              marked_by: null,
              marked_at: null,
              completed_at: null,
            })), { onConflict: 'team_id,robot_class,station,match_id' }));
        }
      }
      // Sync THIS row's busy/failed flags if the reducer changed them.
      const prevQ = activeQueueItem;
      const nextQ = next.queue.find((q) => q.id === ai.queueId);
      if (prevQ && nextQ) {
        const updates: Record<string, unknown> = {};
        if (prevQ.busyAtStation !== nextQ.busyAtStation) updates.busy_at_station = nextQ.busyAtStation;
        // DB invariant: busy_at_station and claimed_by are set/cleared as
        // a pair, so always send both when either changes.
        if (prevQ.claimedBy !== nextQ.claimedBy) updates.claimed_by = nextQ.claimedBy;
        if (prevQ.failed !== nextQ.failed) updates.failed = nextQ.failed;
        if (Object.keys(updates).length > 0) {
          if ('busy_at_station' in updates && !('claimed_by' in updates)) {
            updates.claimed_by = nextQ.claimedBy;
          }
          if ('claimed_by' in updates && !('busy_at_station' in updates)) {
            updates.busy_at_station = nextQ.busyAtStation;
          }
          // Marking releases the claim → clear its heartbeat timestamp too.
          if ('busy_at_station' in updates || 'claimed_by' in updates) {
            updates.claimed_at = nextQ.claimedAt ?? null;
          }
          await wrap(supabase.from('inspection_queue').update(updates).eq('id', nextQ.id));
        }
      }
      return;
    }

    case 'SET_STATION': {
      // Reducer moved (or released) the user's queue claim. Mirror that to the
      // DB. When moving INTO L3, busy_at_station becomes null because L3 isn't
      // a real claim slot. Both busy_at_station and claimed_by stay in sync.
      const prevAI = prev.activeInspection;
      const item = next.queue.find((q) => q.team === prevAI.team && q.match === prevAI.match);
      if (item) {
        const prevItem = prev.queue.find((q) => q.id === item.id);
        if (!prevItem || prevItem.busyAtStation !== item.busyAtStation || prevItem.claimedBy !== item.claimedBy) {
          await wrap(supabase.from('inspection_queue')
            .update({ busy_at_station: item.busyAtStation, claimed_by: item.claimedBy, claimed_at: item.claimedAt ?? null })
            .eq('id', item.id));
        }
      }
      return;
    }

    case 'SELECT_QUEUE_ITEM': {
      // The reducer may have flipped claim state on multiple rows:
      // - cleared the user's prior claim (busy_at_station + claimed_by both null)
      // - set the new claim on the picked item (both fields set)
      // Diff and sync. The DB CHECK ensures both fields stay in lockstep.
      const changed = next.queue.filter((nq) => {
        const pq = prev.queue.find((q) => q.id === nq.id);
        return pq && (pq.busyAtStation !== nq.busyAtStation || pq.claimedBy !== nq.claimedBy);
      });
      await Promise.all(changed.map((q) =>
        wrap(supabase.from('inspection_queue')
          .update({ busy_at_station: q.busyAtStation, claimed_by: q.claimedBy, claimed_at: q.claimedAt ?? null })
          .eq('id', q.id))
      ));
      return;
    }

    case 'SET_QUEUE_ITEM_SKIPPED': {
      // Reducer flipped the skipped flag and may have also released the
      // claim (if the row was being inspected). Send all three together.
      const item = next.queue.find((q) => q.id === action.queueId);
      if (!item) return;
      await wrap(supabase.from('inspection_queue')
        .update({
          skipped: item.skipped,
          busy_at_station: item.busyAtStation,
          claimed_by: item.claimedBy,
          claimed_at: item.claimedAt ?? null,
        })
        .eq('id', action.queueId));
      return;
    }


    case 'ADD_TO_QUEUE': {
      // Per-robot model: the reducer added N rows (one per robot class).
      // Diff and bulk-insert each. DB unique key (team_id, match_id, robot_class)
      // protects against double-insert if the realtime echo races with the
      // operator triggering twice.
      const newItems = next.queue.filter((q) => !prev.queue.some((p) => p.id === q.id));
      if (newItems.length === 0) return;
      const baseTs = Date.now();
      const { error } = await supabase.from('inspection_queue').insert(
        newItems.map((it, i) => ({
          team_id: it.team,
          match_id: it.match,
          robot_class: it.robotClass,
          position: baseTs + i, // monotonic but distinct
          busy_at_station: it.busyAtStation,
          failed: it.failed,
          urgent: it.urgent,
          is_backup: it.isBackup,
          skipped: it.skipped,
        })),
      );
      if (error) throw error;
      // Flip the team's match-side status to at_inspection (one-time per match).
      // EC is single-team; status_red is always the relevant column.
      // 3v3 / 1v1 pick red vs blue based on which side this team is on.
      if (action.event === 'ec') {
        await wrap(supabase.from('match_state')
          .update({ status_red: 'at_inspection' })
          .eq('match_id', newItems[0].match));
      } else {
        const matches = action.event === '3v3' ? next.matches3v3 : next.matches1v1;
        const m = matches.find((mm) => mm.id === newItems[0].match);
        if (m) {
          const isRed = m.red === action.team;
          await wrap(supabase.from('match_state')
            .update({ [isRed ? 'status_red' : 'status_blue']: 'at_inspection' })
            .eq('match_id', newItems[0].match));
        }
      }
      return;
    }

    case 'ADD_TO_PRE_INSPECTION': {
      // Pre-inspection rows: one per robot, no match (match_id NULL), flagged
      // pre_inspection. A partial unique index (team_id, robot_class) WHERE
      // pre_inspection guards against double-add. No match_state to flip.
      const newItems = next.queue.filter((q) => q.preInspection && !prev.queue.some((p) => p.id === q.id));
      if (newItems.length === 0) return;
      const baseTs = Date.now();
      const { error } = await supabase.from('inspection_queue').insert(
        newItems.map((it, i) => ({
          id: it.id, // client-minted UUID → matches the optimistic row (synced by id)
          team_id: it.team,
          match_id: null,
          robot_class: it.robotClass,
          position: baseTs + i,
          is_backup: false,
          pre_inspection: true,
          failed: false,
          urgent: false,
          skipped: false,
        })),
      );
      if (error) throw error;
      return;
    }

    case 'REMOVE_FROM_PRE_INSPECTION': {
      await wrap(supabase.from('inspection_queue')
        .delete()
        .eq('team_id', action.team)
        .eq('pre_inspection', true));
      return;
    }

    case 'SET_PRE_INSPECTION_LEVEL': {
      // Standalone pre-inspection state. Writes go to pre_inspection_level —
      // NOT to robot_inspection_station. The live desk's per-station data is
      // independent of this and is unaffected by edits here.
      await wrap(supabase.from('pre_inspection_level').upsert({
        team_id: action.team,
        event: action.event,
        robot_class: action.robot,
        level: action.status,
        set_by: auth.userId,
        set_at: new Date().toISOString(),
      }, { onConflict: 'team_id,event,robot_class' }));
      return;
    }

    // ============== Transactional ops via Postgres functions ==============
    // Each function wraps its multi-row mutation in a single transaction with
    // SELECT ... FOR UPDATE on event_state. Role gates (staff+ vs ops-only)
    // live in the function body via auth.jwt(). Optimistic local state will
    // diverge briefly; the realtime echo + refetch reconciles within ~1s.

    case 'ADVANCE_MATCH': {
      // advance_match takes per-round outcomes (winner + scores per round).
      // Empty rounds = cold-start advance (no live match yet); the RPC just
      // promotes on-deck. The reducer's optimistic update mirrors that.
      const p_rounds = (action.rounds ?? []).map((r) => ({
        winner: r.winner,
        red_score: r.redScore,
        blue_score: r.blueScore,
      }));
      await wrap(supabase.rpc('advance_match', { p_event: action.event, p_rounds }));
      // Advancing into a real 3v3 match clears the test-match feed toggle so the
      // demo can't resurface when the live slot next empties. Only after the RPC
      // succeeds (wrap throws on error, short-circuiting this).
      if (action.event === '3v3') {
        await wrap(supabase.from('event_state').update({ test_match_3v3: false }).eq('id', 'current'));
      }
      return;
    }

    case 'ADVANCE_TO_NEXT_MATCH': {
      // Head ref's manual "NEXT MATCH" off a bridge-completed match — promote
      // on-deck → live, no scoring (the bridge already finalized it).
      await wrap(supabase.rpc('advance_to_next_match', { p_event: action.event }));
      // Same as ADVANCE_MATCH: turn off the 3v3 test-match feed on advance.
      if (action.event === '3v3') {
        await wrap(supabase.from('event_state').update({ test_match_3v3: false }).eq('id', 'current'));
      }
      return;
    }

    case 'SWAP_NEXT_MATCH':
      await wrap(supabase.rpc('swap_next_match', { p_event: action.event }));
      return;

    case 'FORFEIT_MATCH':
      await wrap(supabase.rpc('forfeit_match', {
        p_match_id: action.matchId,
        p_side: action.side,
      }));
      return;

    case 'SAVE_MATCH_ROUNDS': {
      const p_rounds = action.rounds.map((r) => ({
        winner: r.winner,
        red_score: r.redScore,
        blue_score: r.blueScore,
      }));
      await wrap(supabase.rpc('save_match_rounds', {
        p_match_id: action.matchId,
        p_rounds,
      }));
      return;
    }

    case 'REQUEST_REPLAY':
      await wrap(supabase.rpc('request_replay', { p_match_id: action.matchId }));
      return;

    case 'COMPLETE_EC_MATCH': {
      const p_rounds = action.rounds.map((r) => ({ score: r.score }));
      await wrap(supabase.rpc('ec_complete_match', {
        p_match_id: action.matchId,
        p_rounds,
      }));
      return;
    }

    case 'RESCHEDULE_MATCH':
      await wrap(supabase.rpc('reschedule_match', {
        p_match_id: action.matchId,
        p_after_match_id: action.afterId,
      }));
      return;

    case 'SWAP_MATCHES':
      await wrap(supabase.rpc('swap_matches', {
        p_match_a: action.matchAId,
        p_match_b: action.matchBId,
      }));
      return;

    case 'GENERATE_BRACKET':
      // Ops-only RPC: validates all qual_<event> matches are completed, inserts
      // 15 playoff matches. Seeds come from the qual_standings_<event> view
      // unless the operator supplied a manual override (p_seeds) via the
      // seed-confirmation modal.
      await wrap(supabase.rpc('generate_bracket', {
        p_event: action.event,
        p_seeds: action.seeds ?? null,
        p_times: action.times ?? null,
      }));
      return;

    case 'GENERATE_SWISS_ROUND': {
      // Compare prev / next matches1v1 to find rows the reducer just added —
      // those are the new Swiss-round matches the client computed (Swiss
      // pairing + block-aware time packing live in src/state/swiss.ts). Send
      // them to the server as a payload; insert_qual_matches validates +
      // inserts atomically. No-op if nothing was added (e.g. round cap hit).
      const prevIds = new Set(prev.matches1v1.map((m) => m.id));
      const newRows = next.matches1v1.filter((m) => !prevIds.has(m.id));
      if (newRows.length === 0) return;
      const payload = newRows.map((m) => ({
        id: m.id,
        kind: 'qual_1v1',
        scheduled_at: m.scheduledAt ?? null,
        red_team: m.red,
        // Bye matches have no opponent — send NULL so the server-side RPC
        // recognizes the row as a bye (qual_1v1 + blue_team IS NULL).
        blue_team: m.bye ? null : m.blue,
        field: 'south',
        is_bye: m.bye === true,
      }));
      await wrap(supabase.rpc('insert_qual_matches', { p_matches: payload }));
      return;
    }

    case 'SET_MATCH_TIME': {
      // Single-row time edit. Reuses the bulk RPC with one entry so the
      // server logic stays in one place.
      await wrap(supabase.rpc('set_match_times', {
        p_updates: [{ match_id: action.matchId, scheduled_at: action.scheduledAt }],
      }));
      return;
    }

    case 'APPEND_MATCH_NOTE': {
      // Append a chat-style note. RLS gates by staff+ role and pins
      // author_id = auth.uid() — see migration 31. Empty bodies are
      // dropped client-side (reducer no-ops); the DB CHECK enforces it
      // again as defense in depth.
      const body = action.body.trim();
      if (!body) return;
      if (!auth.userId || !auth.email || !auth.role) {
        throw new Error('APPEND_MATCH_NOTE requires authentication.');
      }
      await wrap(supabase.from('match_notes').insert({
        match_id: action.matchId,
        body,
        author_id: auth.userId,
        author_email: auth.email,
        author_role: auth.role,
      }));
      return;
    }

    case 'APPEND_TEAM_NOTE': {
      // Same pattern as APPEND_MATCH_NOTE — RLS gates volunteer+ insert
      // and pins author_id / author_email / author_role to the JWT.
      const body = action.body.trim();
      if (!body) return;
      if (!auth.userId || !auth.email || !auth.role) {
        throw new Error('APPEND_TEAM_NOTE requires authentication.');
      }
      await wrap(supabase.from('team_notes').insert({
        team_id: action.teamId,
        body,
        author_id: auth.userId,
        author_email: auth.email.toLowerCase(),
        author_role: auth.role,
      }));
      return;
    }

    case 'UPDATE_REFEREE_MODULES': {
      // Ops-only: upsert the affected modules' full counts, then log the change.
      // Two writes, but the screen only fires one per click so it's not hot.
      if (!auth.userId || !auth.email || !auth.role) {
        throw new Error('UPDATE_REFEREE_MODULES requires authentication.');
      }
      if (action.modules.length > 0) {
        await wrap(supabase.from('referee_modules').upsert(
          action.modules.map(({ module, counts }) => ({
            team_id: action.team,
            module_type: module,
            ref_checkout: counts.refCheckout,
            exchange_extras: counts.exchangeExtras,
            ref_return: counts.refReturn,
            updated_by: auth.userId,
            updated_at: new Date().toISOString(),
          })),
          { onConflict: 'team_id,module_type' },
        ));
      }
      await wrap(supabase.from('referee_module_log').insert({
        team_id: action.team,
        body: action.logBody,
        author_id: auth.userId,
        author_email: auth.email.toLowerCase(),
        author_role: auth.role,
      }));
      return;
    }

    case 'SET_ROBOT_WEIGHT': {
      const instance = action.instance ?? 'primary';
      // Canonical store: the robot_weight table (migration 72) — carries the
      // primary/backup instance so a backup Standard no longer clobbers the
      // primary's weight. Upsert the row; kg=null clears it via delete.
      if (action.kg == null) {
        await wrap(supabase.from('robot_weight').delete().match({
          team_id: action.team, event: action.event, robot_class: action.robot, instance,
        }));
      } else {
        // Set set_at explicitly: the column's DEFAULT now() only fires on
        // INSERT, but an upsert that UPDATEs an existing row must bump the
        // timestamp too so the cross-event latest-wins resolution sees the
        // re-weigh as newer.
        await wrap(supabase.from('robot_weight').upsert({
          team_id: action.team, event: action.event, robot_class: action.robot,
          instance, weight_kg: action.kg, set_at: new Date().toISOString(),
        }, { onConflict: 'team_id,event,robot_class,instance' }));
      }
      // Back-compat: also keep the legacy robot_inspection_station.weight_kg
      // s1 column in sync for the PRIMARY instance, so any reader still on the
      // old column (or a pre-migration client) sees the value. Backup writes
      // skip this — the old column has no instance dimension and we don't want
      // a backup weight masquerading as the primary there.
      if (instance === 'primary') {
        await wrap(supabase.from('robot_inspection_station')
          .update({ weight_kg: action.kg })
          .match({
            team_id: action.team,
            event: action.event,
            robot_class: action.robot,
            station: 's1',
          }));
      }
      return;
    }


    case 'MOVE_TO_NEXT_BLOCK': {
      // Cascade: the reducer already repacked the affected matches into new
      // timestamps via packMatchesIntoBlocks. Diff prev vs next and submit
      // the changed rows in a single bulk RPC call.
      const prevByIdRaw = (action.event === '3v3' ? prev.matches3v3 : prev.matches1v1);
      const nextByIdRaw = (action.event === '3v3' ? next.matches3v3 : next.matches1v1);
      const prevById = new Map(prevByIdRaw.map((m) => [m.id, m.scheduledAt ?? null]));
      const updates: Array<{ match_id: string; scheduled_at: string }> = [];
      for (const m of nextByIdRaw) {
        if (!m.scheduledAt) continue;
        if (prevById.get(m.id) !== m.scheduledAt) {
          updates.push({ match_id: m.id, scheduled_at: m.scheduledAt });
        }
      }
      if (updates.length === 0) return;
      await wrap(supabase.rpc('set_match_times', { p_updates: updates }));
      return;
    }

    case 'SET_MATCH_STATUS': {
      // Direct write — RLS gates by role. status_red / status_blue updated independently.
      const column = action.side === 'red' ? 'status_red' : 'status_blue';
      await wrap(supabase.from('match_state')
        .update({ [column]: action.status })
        .eq('match_id', action.matchId));
      return;
    }

    case 'RESET_ROBOT_INSPECTION': {
      const { event, matchId, side, team, robotClass, isBackup } = action;
      if (!auth.userId || !auth.email || !auth.role) {
        throw new Error('RESET_ROBOT_INSPECTION requires authentication.');
      }
      // 1. Reset the robot's persisted progress. Primaries live in the rollup
      //    table; backups carry their progress on their own queue row.
      if (isBackup) {
        await wrap(supabase.from('inspection_queue')
          .update({ s1_result: null, s2_result: null, s3_result: null })
          .eq('team_id', team).eq('match_id', matchId)
          .eq('robot_class', robotClass).eq('is_backup', true));
      } else {
        await wrap(supabase.from('robot_inspection_station')
          .update({ result: 'pending', marked_by: null, marked_at: null })
          .eq('team_id', team).eq('robot_class', robotClass).eq('event', event));
      }
      // 2. Re-enter the team into active inspection. The inspection_queue_lifecycle
      //    trigger re-creates the team's queue rows (ON CONFLICT DO NOTHING), so
      //    the reset robot — now 'pending' in the rollup — reappears at S1, while
      //    the other robots keep their cleared stations. 'at_inspection' is not a
      //    match-ending/not_tracked transition, so the trigger does NOT wipe the
      //    other robots' rollup.
      const column = side === 'red' ? 'status_red' : 'status_blue';
      await wrap(supabase.from('match_state')
        .update({ [column]: 'at_inspection' })
        .eq('match_id', matchId));
      // 3. Stamp both timelines — the match's and the team's.
      await wrap(supabase.from('match_notes').insert({
        match_id: matchId,
        body: `MATCH ${matchId}: Inspection Reset from L3`,
        author_id: auth.userId,
        author_email: auth.email.toLowerCase(),
        author_role: auth.role,
      }));
      await wrap(supabase.from('team_notes').insert({
        team_id: team,
        body: `${robotClassLabel(robotClass)} inspection reset to S1 from L3 (${matchId})`,
        author_id: auth.userId,
        author_email: auth.email.toLowerCase(),
        author_role: auth.role,
      }));
      return;
    }

    // ============== Queue ordering — TODO ==============

    case 'REORDER_QUEUE':
    case 'SORT_QUEUE_BY_MATCH':
      // TODO: persist via fractional ranks. Local-only for now; reorder
      // resets on reload, which is acceptable for prototype-stage usage.
      // eslint-disable-next-line no-console
      console.warn(`[SupabaseStore] ${action.type} not yet persisted (reorder resets on reload).`);
      return;

    // ============== Pure UI state — no DB ==============

    case 'SET_PENDING_WARNING':
    case 'CLEAR_PENDING_WARNING':
      return;

    case 'GO_LIVE_QUALS_3V3': {
      // Captain's-Meeting commit. The reducer already replaced matches3v3
      // and set teamConfig + qualsLive['3v3'] optimistically; now ship the
      // canonical commit to the server in one atomic RPC. Payload is the
      // same shape insert_qual_matches accepts plus the group map. Server
      // raises if quals_3v3_live is already true.
      const groups = Object.entries(action.groups).map(([team_id, group_letter]) => ({
        team_id, group_letter,
      }));
      const matches = action.matches.map((m: Match) => ({
        id: m.id,
        kind: 'qual_3v3',
        scheduled_at: m.scheduledAt ?? null,
        red_team: m.red,
        blue_team: m.blue,
        field: 'north',
        is_bye: false,
      }));
      await wrap(supabase.rpc('go_live_quals_3v3', {
        p_groups: groups, p_matches: matches,
      }));
      return;
    }

    case 'GO_LIVE_QUALS_1V1': {
      const seeds = action.seededTeams.map((team_id, i) => ({
        team_id, seed: i + 1,
      }));
      const matches = action.matches.map((m: Match) => ({
        id: m.id,
        kind: 'qual_1v1',
        scheduled_at: m.scheduledAt ?? null,
        red_team: m.red,
        blue_team: m.bye ? null : m.blue,
        field: 'south',
        is_bye: m.bye === true,
      }));
      await wrap(supabase.rpc('go_live_quals_1v1', {
        p_seeds: seeds, p_matches: matches,
      }));
      return;
    }

    case 'CREATE_PRACTICE_MATCH': {
      // Server enforces the phase (quals live + not begun) and the per-team
      // allowance, generates the P<n> id, and creates match + match_state in
      // one transaction. The optimistic tmp_ row in practiceMatches is
      // replaced by the realtime echo on the matches table.
      await wrap(supabase.rpc('create_practice_match', {
        p_event: action.event,
        p_red: action.red,
        p_blue: action.blue,
        p_scheduled_at: action.scheduledAt ?? null,
      }));
      return;
    }

    case 'SET_PRACTICE_STATUS': {
      // Practice matches have no per-side lifecycle — both columns move
      // together so the EC-style "status = status_red" mirror stays true.
      await wrap(supabase.from('match_state')
        .update({ status_red: action.status, status_blue: action.status })
        .eq('match_id', action.matchId));
      return;
    }

    case 'SET_PRACTICE_TIME': {
      await wrap(supabase.rpc('set_practice_match_time', {
        p_match_id: action.matchId,
        p_scheduled_at: action.scheduledAt,
      }));
      return;
    }

    case 'CANCEL_PRACTICE_MATCH': {
      await wrap(supabase.rpc('cancel_practice_match', { p_match_id: action.matchId }));
      return;
    }

    case 'TOGGLE_TEST_MATCH': {
      // Ops-gated direct event_state write (same path as delays). The 3v3
      // schedule feed serves the test/demo match while this is true.
      await wrap(supabase.from('event_state').update({ test_match_3v3: action.on }).eq('id', 'current'));
      return;
    }

    case 'RECORD_PRACTICE_RESULT': {
      await wrap(supabase.rpc('record_practice_result', {
        p_match_id: action.matchId,
        p_rounds: action.rounds.map((r) => ({
          winner: r.winner, red_score: r.redScore, blue_score: r.blueScore,
        })),
      }));
      return;
    }

    case 'BEGIN_QUALS': {
      await wrap(supabase.rpc('begin_quals', { p_event: action.event }));
      // Pre-inspection ends when quals start — purge leftover pre-inspection
      // rows (match-based queue rows stay; they drive quals inspection).
      const { error } = await supabase.from('inspection_queue').delete().eq('pre_inspection', true);
      if (error) throw error;
      return;
    }

    case 'SET_USER_ACCESS': {
      await wrap(supabase.rpc('set_user_access', {
        p_user_id: action.userId,
        p_role: action.role,
        p_l3: action.l3Override,
        p_captains: action.captainsMeeting,
      }));
      return;
    }

    case 'ADD_VOLUNTEER_EMAIL': {
      await wrap(supabase.rpc('add_volunteer_email', { p_email: action.email }));
      return;
    }

    case 'REMOVE_VOLUNTEER_EMAIL': {
      await wrap(supabase.rpc('remove_volunteer_email', { p_email: action.email }));
      return;
    }
  }
}

async function wrap<T>(promise: PromiseLike<{ data: T; error: { message: string } | null }>): Promise<T> {
  const { data, error } = await promise;
  if (error) throw new Error(error.message);
  return data;
}

// Helpers exposed for unit testing if needed later.
export const __test_only = { FIELD_TO_DB };

// Re-export so consumers can import alongside applyAction.
export type { MatchEvent, Match };
export { ROBOTS_BY_EVENT };
